import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Heading API",
  "title": "Heading",
  "props": ["HeadingProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const Link = makeShortcode("Link");
const HeadingProps = makeShortcode("HeadingProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<PageContent componentName="heading" type="api" mdxType="PageContent">
<LeadParagraph mdxType="LeadParagraph">
  The Heading component can be used with the `level` prop to render the HTML
  heading elements, levels 1-6.
</LeadParagraph>
<h2>{`Basic Usage`}</h2>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Heading } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Heading level={1}>Heading 1</Heading>
      <Heading level={2}>Heading 2</Heading>
      <Heading level={3}>Heading 3</Heading>
      <Heading level={4}>Heading 4</Heading>
      <Heading level={5}>Heading 5</Heading>
      <Heading level={6}>Heading 6</Heading>
    </>
  );
}
`}</code></pre>
<h2>{`Size`}</h2>
<p>{`There are six visual styles for the heading: `}<inlineCode parentName="p">{`headingXLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingMedium`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingSmall`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingXSmall`}</inlineCode>{` and `}<inlineCode parentName="p">{`heading2XSmall`}</inlineCode>{`. If not specified, a corresponding visual style will be applied based on the heading.`}</p>
<p>{`Visual style and heading level can be set independently so you can specify the correct semantic level and then apply any of the visual styles to change the visual appearance to fit your design.`}</p>
<p>{`Furthermore, the `}<inlineCode parentName="p">{`visualStyle`}</inlineCode>{` prop can also accept the values for the four different paragraph visual styles: `}<inlineCode parentName="p">{`bodyLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`bodyMedium`}</inlineCode>{`, `}<inlineCode parentName="p">{`bodySmall`}</inlineCode>{`, and `}<inlineCode parentName="p">{`bodyXSmall`}</inlineCode>{`. These are sizes are also used with the `}<Link to="/api/paragraph" mdxType="Link">{`Paragraph`}</Link>{` component.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Heading, TypographyVisualStyle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Heading level={2} visualStyle={TypographyVisualStyle.headingXLarge}>
        H2 styled as Heading XLarge
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.headingLarge}>
        H2 styled as Heading Large
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.headingMedium}>
        H2 styled as Heading Medium
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.headingSmall}>
        H2 styled as Heading Small
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.headingXSmall}>
        H2 styled as Heading XSmall
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.heading2XSmall}>
        H2 styled as Heading 2XSmall
      </Heading>

      <Heading level={2} visualStyle={TypographyVisualStyle.bodyLarge}>
        H2 styled as Body Large
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.bodyMedium}>
        H2 styled as Body Medium
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.bodySmall}>
        H2 styled as Body Small
      </Heading>
      <Heading level={2} visualStyle={TypographyVisualStyle.bodyXSmall}>
        H2 styled as Body XSmall
      </Heading>
    </>
  );
}
`}</code></pre>
<h2>{`Context Variant`}</h2>
<p>{`The `}<inlineCode parentName="p">{`contextVariant`}</inlineCode>{` prop can be used to set additional styles on a heading component. The `}<inlineCode parentName="p">{`contextVariant`}</inlineCode>{` can affect the font-size, text-transform, or font-family. The default `}<inlineCode parentName="p">{`contextVariant`}</inlineCode>{` is `}<inlineCode parentName="p">{`default`}</inlineCode>{`.`}</p>
<p>{`The `}<inlineCode parentName="p">{`expressive`}</inlineCode>{` context variant can be used in marketing contexts, which may require more variety than the default styles.`}</p>
<p>{`The `}<inlineCode parentName="p">{`narrative`}</inlineCode>{` context variant can be used for content-heavy contexts, such as a blog post or an e-reader.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Heading, TypographyContextVariant } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <div style={{ marginBottom: '40px' }}>
        <Heading contextVariant={TypographyContextVariant.expressive} level={1}>
          Expressive Heading 1
        </Heading>
        <Heading contextVariant={TypographyContextVariant.expressive} level={2}>
          Expressive Heading 2
        </Heading>
        <Heading contextVariant={TypographyContextVariant.expressive} level={3}>
          Expressive Heading 3
        </Heading>
        <Heading contextVariant={TypographyContextVariant.expressive} level={4}>
          Expressive Heading 4
        </Heading>
        <Heading contextVariant={TypographyContextVariant.expressive} level={5}>
          Expressive Heading 5
        </Heading>
        <Heading contextVariant={TypographyContextVariant.expressive} level={6}>
          Expressive Heading 6
        </Heading>
      </div>
      <div>
        <Heading contextVariant={TypographyContextVariant.narrative} level={1}>
          Narrative Heading 1
        </Heading>
        <Heading contextVariant={TypographyContextVariant.narrative} level={2}>
          Narrative Heading 2
        </Heading>
        <Heading contextVariant={TypographyContextVariant.narrative} level={3}>
          Narrative Heading 3
        </Heading>
        <Heading contextVariant={TypographyContextVariant.narrative} level={4}>
          Narrative Heading 4
        </Heading>
        <Heading contextVariant={TypographyContextVariant.narrative} level={5}>
          Narrative Heading 5
        </Heading>
        <Heading contextVariant={TypographyContextVariant.narrative} level={6}>
          Narrative Heading 6
        </Heading>
      </div>
    </>
  );
}
`}</code></pre>
<h2>{`Color`}</h2>
<p>{`The `}<inlineCode parentName="p">{`color`}</inlineCode>{` prop can be used to change the color of the heading to convey additional meaning. It accepts the values: `}<inlineCode parentName="p">{`success`}</inlineCode>{`, `}<inlineCode parentName="p">{`danger`}</inlineCode>{` and `}<inlineCode parentName="p">{`subdued`}</inlineCode>{`.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Heading, TypographyColor } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Heading color={TypographyColor.success} level={2}>
        Success Color Heading
      </Heading>

      <Heading color={TypographyColor.danger} level={2}>
        Danger Color Heading
      </Heading>

      <Heading color={TypographyColor.subdued} level={2}>
        Subdued Color Heading
      </Heading>
    </>
  );
}
`}</code></pre>
<h2>{`Inverse`}</h2>
<p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean that changes the colors so the component can appear on a dark background.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Card, CardBody, Heading, TypographyColor } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Heading isInverse level={2}>
          Heading 2
        </Heading>

        <Heading color={TypographyColor.success} isInverse level={2}>
          Success Color Heading
        </Heading>

        <Heading color={TypographyColor.danger} isInverse level={2}>
          Danger Color Heading
        </Heading>

        <Heading isInverse level={2} color={TypographyColor.subdued}>
          Heading 2 Subdued
        </Heading>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
<h2>{`No Margins`}</h2>
<p>{`By default, the `}<inlineCode parentName="p">{`Heading`}</inlineCode>{` component includes margins on the top and bottom. When the `}<inlineCode parentName="p">{`noMargins`}</inlineCode>{` prop is applied, the element will have a margin value of 0.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Heading } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Heading noMargins level={1}>
        Heading 1
      </Heading>
      <Heading noMargins level={2}>
        Heading 2
      </Heading>
      <Heading noMargins level={3}>
        Heading 3
      </Heading>
      <Heading noMargins level={4}>
        Heading 4
      </Heading>
      <Heading noMargins level={5}>
        Heading 5
      </Heading>
      <Heading noMargins level={6}>
        Heading 6
      </Heading>
    </>
  );
}
`}</code></pre>
<h2>{`Forward Ref`}</h2>
<p>{`Using React's `}<inlineCode parentName="p">{`forwardRef`}</inlineCode>{` feature you can gain access to the reference of the internal heading.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Button, Heading } from 'react-magma-dom';

export function Example() {
  const headingRef = React.useRef<HTMLHeadingElement>();

  function handleClick() {
    headingRef.current.tabIndex = -1;
    headingRef.current.focus();
  }

  return (
    <>
      <Heading ref={headingRef} level={1}>
        Heading to Be Focused
      </Heading>

      <Button onClick={handleClick}>Click to focus heading</Button>
    </>
  );
}
`}</code></pre>
<h2>{`Testing`}</h2>
<p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the heading element for easier querying in tests.`}</p>
<pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<Heading testId="test-id">Heading 1</Heading>
`}</code></pre>
<h2>{`Heading Props`}</h2>
<p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the heading element.`}</strong></p>
<p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
        }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the heading (h1-6) element that gets rendered.`}</p>
<HeadingProps mdxType="HeadingProps" />
</PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      